// frontend/src/pages/ChatInterface.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { databaseAPI, queryAPI } from '../services/api';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import NavBar from '../components/NavBar';
import TableSelectionModal from '../components/TableSelectionModal';
import ErrorRecoveryCard from '../components/ErrorRecoveryCard';

const API_URL = API_BASE_URL;

const ChatInterface = () => {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [dbInfo, setDbInfo] = useState(null);
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [showSchema, setShowSchema] = useState(false);
  const [schemaLoaded, setSchemaLoaded] = useState(false);
  
  // Error recovery state
  const [showTableSelection, setShowTableSelection] = useState(false);
  const [tableSelectionData, setTableSelectionData] = useState(null);
  const messagesEndRef = useRef(null);

  const sampleQuestions = [
    "How many records are in each table?",
    "Show me the top 10 most recent records",
    "Find all columns that contain email addresses", 
    "What's the average value of numeric columns?",
    "Show me tables with the most data"
  ];

  // Fetch current database info
  useEffect(() => {
    const fetchDbInfo = async () => {
      try {
        const response = await databaseAPI.list();
        const db = response.databases.find(db => db.id === connectionId);
        
        if (db) {
          setDbInfo(db);
          
          // Add welcome message
          setMessages([{
            type: 'system',
            content: `Connected to ${db.database_name} (${db.database_type}). Ask me anything about your data!`
          }, {
            type: 'system',
            content: `Loading database schema, please wait...`
          }]);

          // Fetch schema for this database
          fetchSchema(connectionId);
        } else {
          // Database not found, redirect to dashboard
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error fetching database info:', error);
      }
    };

    const fetchSchema = async (id) => {
      setSchemaLoading(true);
      try {
        const response = await databaseAPI.getSchema(id);
        setSchema(response.schema);
        
        // Add schema loaded message
        setMessages(prev => [
          ...prev.slice(0, -1), // Remove the "Loading schema" message
          {
            type: 'system',
            content: `✅ Database schema loaded successfully! You can now ask questions about your data.`
          }
        ]);
        
        // Enable the send button
        setSchemaLoaded(true);
      } catch (error) {
        console.error('Error fetching schema:', error);
        setMessages(prev => [
          ...prev.slice(0, -1), // Remove the "Loading schema" message
          {
            type: 'error',
            content: `Failed to load database schema. You can still ask questions, but results may be limited.`
          }
        ]);
      } finally {
        setSchemaLoading(false);
      }
    };

    if (connectionId) {
      fetchDbInfo();
    }
  }, [connectionId, navigate]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !schemaLoaded) return;

    const userQuery = input;
    setInput('');
    
    // Add user message
    setMessages(prev => [...prev, { type: 'user', content: userQuery }]);
    
    setLoading(true);
    let sqlQuery = '';
    
    try {
      // Get last 5 messages for context (excluding system messages)
      const conversationHistory = messages
        .filter(msg => msg.type === 'user' || (msg.type === 'system' && msg.results))
        .slice(-5)
        .map(msg => ({
          type: msg.type,
          content: msg.content,
          sql: msg.sql || null
        }));
      
      // Always use the improved query generation
      // Let the AI handle the query directly
      const generateResponse = await queryAPI.generate(userQuery, connectionId, 'deepseek-v3');
      
      sqlQuery = generateResponse.sql;
      
      // Add SQL message
      setMessages(prev => [...prev, { 
        type: 'sql', 
        content: sqlQuery 
      }]);
      
      // Execute the SQL query with improved validation
      const executeResponse = await queryAPI.execute(sqlQuery, connectionId);
      
      const results = executeResponse.data;
      const validation = executeResponse.validation;
      
      // Check for NULL results or error analysis
      if (validation.is_null_result || validation.error_analysis) {
        // Add error recovery message
        setMessages(prev => [...prev, { 
          type: 'error_recovery', 
          content: validation.error_analysis?.user_guidance?.message || 'Query returned null results.',
          sql: sqlQuery,
          validation,
          error_analysis: validation.error_analysis,
          original_question: input
        }]);
      } else {
        // Prepare message content with validation info
        let messageContent = `Found ${results.length} results.`;
        if (validation.warnings && validation.warnings.length > 0) {
          messageContent += '\n⚠️ ' + validation.warnings.join('\n⚠️ ');
        }
        if (validation.suggestions && validation.suggestions.length > 0) {
          messageContent += '\n💡 ' + validation.suggestions.join('\n💡 ');
        }
        
        // Add response message
        setMessages(prev => [...prev, { 
          type: 'system', 
          content: messageContent,
          results,
          sql: sqlQuery,
          validation
        }]);
      }
    } catch (error) {
      // Check if error has recovery information
      const errorData = error.response?.data;
      if (errorData?.error_recovery) {
        setMessages(prev => [...prev, { 
          type: 'error_recovery', 
          content: errorData.error_recovery.conversation?.message || 'An error occurred.',
          sql: sqlQuery,
          error_recovery: errorData.error_recovery,
          original_question: input,
          error_message: errorData.error
        }]);
      } else {
        // Add regular error message
        setMessages(prev => [...prev, { 
          type: 'error', 
          content: errorData?.error || 'An error occurred while processing your query.'
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (sql) => {
    try {
      const response = await axios.post(`${API_URL}/query/export-excel`, {
        query: sql,
        connection_id: connectionId
      });
      
      // Create and trigger download
      const link = document.createElement('a');
      link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_data}`;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting Excel:', error);
    }
  };

  const handleTableSelectionRequested = (data) => {
    setTableSelectionData(data);
    setShowTableSelection(true);
  };

  const handleTablesSelected = async (selectedTables) => {
    setLoading(true);
    try {
      // Generate focused query using selected tables
      const response = await axios.post(`${API_URL}/v2/query/generate-focused`, {
        question: tableSelectionData.originalQuestion,
        connection_id: connectionId,
        selected_tables: selectedTables,
        original_error: tableSelectionData.errorContext?.original_error || '',
        model_name: 'deepseek-v3'
      });

      const sqlQuery = response.data.sql;

      // Add message about table selection
      setMessages(prev => [...prev, { 
        type: 'system', 
        content: `🎯 Generated focused query using selected tables: ${selectedTables.join(', ')}`,
        context: 'table_selection'
      }]);

      // Add SQL message
      setMessages(prev => [...prev, { 
        type: 'sql', 
        content: sqlQuery 
      }]);

      // Execute the focused query
      const executeResponse = await axios.post(`${API_URL}/v2/query/execute`, {
        query: sqlQuery,
        connection_id: connectionId,
        user_question: tableSelectionData.originalQuestion
      });

      const results = executeResponse.data;
      const validation = executeResponse.validation;

      // Add response message
      let messageContent = `Found ${results.length} results.`;
      setMessages(prev => [...prev, { 
        type: 'system', 
        content: messageContent,
        results,
        sql: sqlQuery,
        validation
      }]);

    } catch (error) {
      const errorData = error.response?.data;
      setMessages(prev => [...prev, { 
        type: 'error', 
        content: errorData?.error || 'An error occurred while generating the focused query.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (message, index) => {
    switch (message.type) {
      case 'user':
        return (
          <div key={index} className="flex justify-end mb-4">
            <div className="bg-violet-600 text-white p-4 rounded-lg max-w-3xl break-words">
              {message.content}
            </div>
          </div>
        );
      
      case 'sql':
        return (
          <div key={index} className="mb-4">
            <div className="flex items-center mb-2">
              <span className="text-sm text-gray-400">🔍 Generated SQL</span>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <pre className="text-green-400 text-sm whitespace-pre-wrap break-words overflow-hidden">{message.content}</pre>
            </div>
          </div>
        );
      
      case 'system':
        return (
          <div key={index} className="mb-4">
            <div className="bg-gray-800/50 p-4 rounded-lg border border-violet-800/30">
              <div className="text-gray-300 whitespace-pre-line mb-3">{message.content}</div>
              
              {message.results && message.results.length > 0 && (
                <div className="mt-4">
                  <div className="overflow-x-auto max-w-full">
                    <table className="w-full text-sm min-w-full">
                      <thead>
                        <tr className="border-b border-gray-700">
                          {Object.keys(message.results[0]).map((key) => (
                            <th key={key} className="text-left p-2 text-violet-300 font-medium whitespace-nowrap">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {message.results.slice(0, 10).map((row, idx) => (
                          <tr key={idx} className="border-b border-gray-800">
                            {Object.values(row).map((value, cellIdx) => (
                              <td key={cellIdx} className="p-2 text-gray-300 max-w-xs break-words">
                                <div className="truncate" title={value?.toString() || 'NULL'}>
                                  {value?.toString() || 'NULL'}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {message.results.length > 10 && (
                    <p className="text-gray-500 text-sm mt-2">
                      Showing first 10 of {message.results.length} results
                    </p>
                  )}
                  
                  {message.sql && (
                    <button
                      onClick={() => handleExportExcel(message.sql)}
                      className="mt-3 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
                    >
                      📊 Export to Excel
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      
      case 'comprehensive':
        return (
          <div key={index} className="mb-4">
            <div className="bg-blue-900/30 border border-blue-700 p-4 rounded-lg">
              <div className="text-blue-300 mb-4">{message.content}</div>
              
              {message.comprehensiveResults && message.comprehensiveResults.map((result, idx) => (
                <div key={idx} className="mb-4 p-3 bg-gray-800/50 rounded border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-violet-300 font-medium">{result.description}</h4>
                    <span className="text-xs text-gray-400">{result.relevance} relevance</span>
                  </div>
                  
                  {/* Handle clarification scenarios */}
                  {result.context === 'clarification_needed' ? (
                    <div className="bg-yellow-900/30 border border-yellow-700 p-3 rounded mb-3">
                      <div className="text-yellow-300 mb-2">🤔 {result.description}</div>
                      
                      {/* Show conversation options if available */}
                      {result.conversation_options && result.conversation_options.map((option, optIdx) => (
                        <div key={optIdx} className="mb-2">
                          <button
                            onClick={() => {
                              if (option.action.startsWith('SELECT')) {
                                // Direct SQL query
                                setInput(option.description);
                              } else {
                                // Action-based query
                                setInput(option.text);
                              }
                            }}
                            className="w-full text-left p-3 bg-blue-800/30 hover:bg-blue-700/40 border border-blue-600 rounded text-blue-200 text-sm transition-colors"
                          >
                            <div className="font-medium">{option.text}</div>
                            <div className="text-xs text-blue-300 mt-1">{option.description}</div>
                          </button>
                        </div>
                      ))}
                      
                      {/* Legacy order_tables support */}
                      {result.order_tables && result.order_tables.map(([tableName, emailCol, count], tableIdx) => (
                        <div key={tableIdx} className="mb-2">
                          <button
                            onClick={() => setInput(`Show orders from ${tableName} for ${message.userQuery.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/)?.[0] || 'specific email'}`)}
                            className="w-full text-left p-2 bg-blue-800/30 hover:bg-blue-700/40 border border-blue-600 rounded text-blue-200 text-sm transition-colors"
                          >
                            📋 <strong>{tableName}</strong> ({count} records) - Email column: {emailCol}
                          </button>
                        </div>
                      ))}
                      
                      {/* Show follow-up suggestion if available */}
                      {result.follow_up && (
                        <div className="mt-3 p-2 bg-gray-800/50 rounded text-gray-300 text-sm">
                          💡 <em>{result.follow_up}</em>
                        </div>
                      )}
                    </div>
                  ) : result.error ? (
                    <div className="text-red-400 text-sm">{result.error}</div>
                  ) : result.data && result.data.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-600">
                            {Object.keys(result.data[0]).map((key) => (
                              <th key={key} className="text-left p-2 text-violet-300 font-medium">
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.slice(0, 5).map((row, rowIdx) => (
                            <tr key={rowIdx} className="border-b border-gray-700">
                              {Object.values(row).map((value, cellIdx) => (
                                <td key={cellIdx} className="p-2 text-gray-300">
                                  {value?.toString() || 'NULL'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {result.data.length > 5 && (
                        <p className="text-gray-500 text-xs mt-2">
                          Showing first 5 of {result.data.length} results from {result.table}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">No data found in {result.table}</div>
                  )}
                  
                  {result.query !== '-- Multiple order tables found' && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                        Show SQL Query
                      </summary>
                      <pre className="text-xs text-green-400 mt-1 bg-gray-900 p-2 rounded overflow-x-auto">
                        {result.query}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div key={index} className="mb-4">
            <div className="bg-red-900/30 border border-red-700 p-4 rounded-lg text-red-300">
              {message.content}
            </div>
          </div>
        );

      case 'error_recovery':
        return (
          <div key={index} className="mb-4">
            <ErrorRecoveryCard 
              errorRecovery={message.error_recovery || {
                conversation: {
                  title: "Query Issue",
                  message: message.content,
                  icon: "⚠️",
                  suggested_tables: message.error_analysis?.suggested_tables || [],
                  original_query: message.sql,
                  original_question: message.original_question
                },
                error_analysis: message.error_analysis
              }}
              onTableSelectionRequested={handleTableSelectionRequested}
              onRetryRequested={() => {
                setInput(message.original_question);
                // Will trigger submit when input is set
              }}
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900 overflow-x-hidden">
      <NavBar />
      
      <div className="flex h-[calc(100vh-80px)] overflow-hidden relative">
        {/* Main Chat Area */}
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${showSchema ? 'mr-80' : 'mr-0'}`}>
          {/* Header */}
          <div className="bg-gray-900/50 backdrop-blur-sm border-b border-violet-800/30 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  ← Back
                </button>
                <span className="text-2xl">🗄️</span>
                <div>
                  <h1 className="text-xl font-semibold text-white">
                    {dbInfo ? dbInfo.database_name : 'Loading...'}
                  </h1>
                  <p className="text-sm text-gray-400">
                    {dbInfo ? `${dbInfo.database_type} • ${dbInfo.host}` : ''}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowSchema(!showSchema)}
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
              >
                {showSchema ? 'Hide Schema' : 'Show Schema'}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-4 bg-gradient-to-b from-slate-900/50 to-violet-900/30">
            {messages.map((message, index) => renderMessage(message, index))}
            
            {loading && (
              <div className="flex items-center space-x-2 text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-500"></div>
                <span>Generating response...</span>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <div className="bg-gray-900/50 backdrop-blur-sm border-t border-violet-800/30 p-4">
            <form onSubmit={handleSubmit} className="flex space-x-4">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={!schemaLoaded ? "Please wait for schema to load..." : "Ask a question about your data..."}
                disabled={loading || !schemaLoaded}
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
              />
              <button
                type="submit"
                disabled={loading || !input.trim() || !schemaLoaded}
                className="px-6 py-3 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                title={!schemaLoaded ? "Please wait for schema to load..." : ""}
              >
                Send →
              </button>
            </form>
          </div>
        </div>

        {/* Sidebar Backdrop */}
        {showSchema && (
          <div 
            className="fixed inset-0 bg-black/20 z-5 top-20"
            onClick={() => setShowSchema(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <div className={`w-80 bg-gray-900/90 backdrop-blur-sm border-l border-violet-800/30 transition-all duration-300 flex-shrink-0 ${showSchema ? 'translate-x-0' : 'translate-x-full'} fixed right-0 top-20 h-[calc(100vh-80px)] z-10`}>
          <div className="p-4 h-full overflow-y-auto overflow-x-hidden">
            {/* Sample Questions */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-white mb-3">Sample Questions</h3>
              <div className="space-y-2">
                {sampleQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(question)}
                    className="w-full text-left p-3 bg-violet-900/20 hover:bg-violet-800/30 border border-violet-800/30 rounded-lg text-gray-300 text-sm transition-colors break-words whitespace-normal"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            {/* Database Schema */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                🗄️ Database Schema
              </h3>
              
              {schemaLoading ? (
                <div className="text-gray-400">Loading schema...</div>
              ) : schema ? (
                <div className="space-y-2">
                  {Object.entries(schema).map(([tableName, tableInfo]) => (
                    <details key={tableName} className="bg-gray-800/30 rounded-lg">
                      <summary className="p-3 cursor-pointer text-violet-300 hover:bg-gray-700/30 rounded-lg">
                        🗄️ {tableName}
                      </summary>
                      <div className="p-3 pt-0">
                        <div className="text-xs space-y-1">
                          {tableInfo.columns?.map((col, idx) => (
                            <div key={idx} className="text-gray-400">
                              <span className="text-gray-300">{col.name}</span>
                              <span className="text-violet-400 ml-2">({col.type})</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <div className="text-gray-400">No schema available</div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Table Selection Modal */}
      <TableSelectionModal 
        isOpen={showTableSelection}
        onClose={() => setShowTableSelection(false)}
        suggestedTables={tableSelectionData?.suggestedTables || []}
        onTablesSelected={handleTablesSelected}
        title="Select Tables for Query"
        message="Choose the tables that best match your question to generate a focused query:"
        icon="🎯"
      />
    </div>
  );
};

export default ChatInterface;